import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  InformationCircleIcon,
  LockClosedIcon,
  SparklesIcon,
  TableCellsIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import TestPreview from "../../pages/Exams/TestBuilder/TestPreview";
import HelpButton from "../Help/HelpButton";
import VariantsGuide from "../Help/guides/VariantsGuide";
import OverflowMenu from "../ui/OverflowMenu";
import {
  addPassageToVariant,
  addToVariant,
  confirmVariant,
  deleteSubjectVariant,
  proposeVariant,
  removeFromVariant,
  removePassageFromVariant,
  replaceInVariant,
  unconfirmVariant,
  updateSubjectVariant,
} from "../../services/subjectVariantService";
import { getApiErrorCode } from "../../utils/apiError";
import { formatDateTime } from "../../utils/formatDate";
import { cardClass, focusRing, iconButtonClass, linkButtonClass, Spinner, useMediaQuery } from "../../pages/Exams/TestBuilder/ui";
import useVariant from "./useVariant";
import VariantComposition from "./VariantComposition";
import VariantCheck from "./VariantCheck";
import CandidatesDialog from "./CandidatesDialog";
import { ConfirmDialog, DeleteDialog, QuestionDialog, SettingsDialog, UnconfirmDialog } from "./VariantDialogs";
import {
  accountName,
  badgeClass,
  BookletChips,
  headerPrimaryClass,
  headerQuietClass,
  LockedBadge,
  secondaryButtonClass,
  VariantStatusBadge,
} from "./variantUi";
import {
  bankHref,
  blockingLines,
  byCodes,
  findRow,
  hasNoTopic,
  isOutOfReach,
  isUnreadable,
  LEVELS,
  passageCodes,
  passageNames,
  placeKey,
  questionRefs,
  rowKey,
  rowPlace,
  rowWhen,
  variantErrorMessage,
  weekKey,
} from "./variantText";
import { variantChanges } from "./variantChanges";

const FLASH_MS = 1600;
// A new proposal ("Propose again") draws another set: the server's seeds are 0 … 2^31 − 1.
const randomSeed = () => Math.floor(Math.random() * 2147483647);
const reduceMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const dismissButtonClass = `${iconButtonClass} -my-1 -mr-2 h-8 w-8 max-sm:h-11 max-sm:w-11`;
// The check's line under the header's actions (a way to the check): as wide as its text, in the check's colour, red
// while something blocks the confirmation and green once nothing does, also on hover.
const checkLineClass = (ok) =>
  `inline-flex items-center gap-1 self-start rounded-lg px-1.5 py-1 text-sm font-medium transition duration-200 2xl:self-end ${focusRing} ${
    ok ? "text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800" : "text-red-700 hover:bg-red-50 hover:text-red-800"
  }`;
const escapeHtml = (text) => String(text).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
// A free place's key (placeKey) once its row came back as `entry` (the matrix was saved meanwhile: another uuid, maybe
// other subtopics or counts): the same place while it is still free there, else the level's first free one.
const movedSlotKey = (slotKey, entry, level) => {
  const index = Number(String(slotKey).split(":").pop());
  const filled = (entry.test_questions?.[level] || []).length;
  return placeKey(entry.row, level, index >= filled && index < (entry.planned?.[level] || 0) ? index : filled);
};
// What a row plans in all: A + B + C.
const plannedCount = (entry) => LEVELS.reduce((sum, level) => sum + (entry.planned?.[level] || 0), 0);
// The server's answers that the matrix lost topics since the page read the variant (else proposing or confirming
// would already say why it waits): the page reads it again to show those weeks.
const TOPIC_CODES = ["matrix_without_topics", "row_without_topic"];

/**
 * The variant as the student's view (TestPreview) shows it, named by its printed heading. A question the user no
 * longer reads comes without its text and options: its card says what it is instead, with its place's difficulty
 * (the check placed it by its difficulty), and without its passage, which is out of reach as well.
 */
function studentView(t, detail) {
  const levels = new Map();
  (detail.check?.rows || []).forEach((entry) =>
    LEVELS.forEach((level) => (entry.test_questions?.[level] || []).forEach((uuid) => levels.set(uuid, level)))
  );
  const body = `<p><strong>${escapeHtml(t("variants.unreadable.title"))}</strong></p><p>${escapeHtml(t("variants.unreadable.hint"))}</p>`;
  return {
    ...detail,
    name: detail.heading?.trim() || detail.name,
    test_questions: (detail.test_questions || []).map((tq) =>
      isUnreadable(tq.question)
        ? {
            ...tq,
            question: { uuid: tq.question.uuid, code: tq.question.code, body, answer_keys: [], passage: null, difficulty: levels.get(tq.uuid) || null },
          }
        : tq
    ),
  };
}

// A proposal's shortfalls as lines: one per row and level, and one per week without a topic for all its levels and
// rows ({row, levels: [{level, count}]}, named by the week): its places wait for the matrix to name a topic, not for
// the bank.
function shortfallLines(shortfalls) {
  const lines = [];
  const topicless = new Map();
  shortfalls.forEach((item) => {
    const count = Math.max(0, (item.planned ?? 0) - (item.proposed ?? 0));
    if (!hasNoTopic(item.row)) {
      lines.push({ row: item.row, levels: [{ level: item.level, count }], available: item.available ?? 0 });
      return;
    }
    const key = weekKey(item.row);
    if (!topicless.has(key)) {
      topicless.set(key, { row: item.row, levels: [], noTopic: true });
      lines.push(topicless.get(key));
    }
    const { levels } = topicless.get(key);
    const same = levels.find((entry) => entry.level === item.level);
    if (same) same.count += count;
    else levels.push({ level: item.level, count });
  });
  lines.forEach((line) => {
    if (line.noTopic) line.levels.sort((a, b) => LEVELS.indexOf(a.level) - LEVELS.indexOf(b.level));
  });
  return lines;
}

// What the last proposal could not fill: "Proposed 18/20; missing: B×1 (II quarter, week 3, Topic › Subtopic)".
// A week without a topic says so, with the way to the matrix (`matrixHref`) instead of the bank.
function ProposalPanel({ proposal, folderUuid, matrixHref, onClose }) {
  const [t] = useTranslation();
  const short = proposal.shortfalls.length > 0;
  const lines = useMemo(() => shortfallLines(proposal.shortfalls), [proposal.shortfalls]);
  return (
    <div
      className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
        short ? "border-amber-200 bg-amber-50 text-amber-950" : "border-emerald-200 bg-emerald-50 text-emerald-950"
      }`}
    >
      {short ? (
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
      ) : (
        <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {t("variants.proposal.summary", { proposed: proposal.proposed, planned: proposal.planned })}
          {short ? t("variants.proposal.missing") : ` — ${t("variants.proposal.complete")}`}
        </p>
        {short && (
          <ul className="mt-1 space-y-1">
            {lines.map((line, index) => {
              const href = line.noTopic ? null : bankHref(folderUuid, line.row, line.levels[0].level);
              return (
                <li key={index} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 [overflow-wrap:anywhere]">
                  <span>
                    {line.noTopic
                      ? `${line.levels.map(({ level, count }) => `${level}×${count}`).join(", ")} (${rowWhen(t, line.row) || t("variants.row.no_week")})`
                      : t("variants.proposal.shortfall", { level: line.levels[0].level, count: line.levels[0].count, place: rowPlace(t, line.row) })}
                  </span>
                  <span className="text-xs text-amber-800">
                    {line.noTopic ? t("variants.proposal.no_topic") : t("variants.proposal.available", { count: line.available })}
                  </span>
                  {line.noTopic ? (
                    <Link to={matrixHref} className={`${linkButtonClass} py-0.5 text-xs max-sm:min-h-11`}>
                      <TableCellsIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("variants.list.open_matrix")}
                    </Link>
                  ) : (
                    href && (
                      <a href={href} target="_blank" rel="noopener noreferrer" className={`${linkButtonClass} py-0.5 text-xs max-sm:min-h-11`}>
                        {t("variants.find_in_bank")}
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      </a>
                    )
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {proposal.reused > 0 && <p className="mt-1 text-xs opacity-80">{t("variants.proposal.reused", { count: proposal.reused })}</p>}
      </div>
      <button type="button" onClick={onClose} aria-label={t("variants.proposal.dismiss")} className={dismissButtonClass}>
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

// Someone else's change that a write of this page ran into (409 variant_changed): what it was, in plain words.
function ChangeNotice({ notice, onClose }) {
  const [t] = useTranslation();
  return (
    <div role="status" className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
      <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{t("variants.changed.title")}</p>
        <p className="mt-0.5 text-amber-900">{t("variants.changed.text")}</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-5 [overflow-wrap:anywhere]">
          {notice.lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
          {notice.more > 0 && <li>{t("variants.changed.more", { count: notice.more })}</li>}
        </ul>
      </div>
      <button type="button" onClick={onClose} aria-label={t("variants.changed.dismiss")} className={dismissButtonClass}>
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

// "18 / 20" in the header: what the variant has against its plan.
function Stat({ label, value, planned }) {
  const off = planned != null && value !== planned;
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-slate-500">{label}</dt>
      <dd className="tabular-nums">
        <span className={`font-semibold ${off ? "text-amber-700" : "text-slate-900"}`}>{value ?? 0}</span>
        {planned != null && <span className="text-slate-500"> / {planned}</span>}
      </dd>
    </div>
  );
}

/**
 * A subject variant: its header (name with the guide's "?", printed heading, grade, status, booklets) with its
 * actions — the status's step (propose, confirm or back to draft), propose again and the student's view beside it,
 * settings and delete in "⋯" —, its composition laid out by the matrix and its check. A variant in an active
 * booklet (`locked`) is read only. Every write sends the revision the user acted on (useVariant); one refused
 * because someone else changed the variant meanwhile reads it again, says so and lists what changed. `autoFocus`:
 * opened from the list or created in this tab, so the focus goes to its title.
 */
export default function VariantDetail({ uuid, folder, matrix, onBack, autoFocus = false }) {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const location = useLocation();
  const noPlanId = useId();
  const checkLineId = useId();
  // The header's breakpoint (OverflowMenu's): the everyday actions quiet beside the step from md up, in "⋯" below.
  const wide = useMediaQuery("(min-width: 768px)");
  const { loading, error, detail, pending, write, reload, retry, since, latest, setCheck } = useVariant(uuid);
  // {type, open, revision (the variant's when it opened), ...its data}: the data stays while the dialog fades out.
  const [dialog, setDialog] = useState(null);
  const [running, setRunning] = useState(null); // the header's write: "propose" | "confirm" | "unconfirm"
  const [proposal, setProposal] = useState(null);
  const [refused, setRefused] = useState(false);
  const [preview, setPreview] = useState(null);
  const [flashIds, setFlashIds] = useState(() => new Set());
  const [announcement, setAnnouncement] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Someone else's change a write of this page ran into: {lines, more, tqs, emptied}, until dismissed or the next
  // write of this page.
  const [changeNotice, setChangeNotice] = useState(null);
  const rootRef = useRef(null);
  const actionsRef = useRef(null);
  // The last header action came from "⋯" (whose item is gone once its dialog or preview closes).
  const fromMenuRef = useRef(false);
  const checkRef = useRef(null);
  const titleRef = useRef(null);
  const retryRef = useRef(null);
  const retriedRef = useRef(false);
  const focusedRef = useRef(false);
  const flashTimer = useRef(null);
  // The detail as it is now, for the answers of writes started on an older one.
  const detailRef = useRef(detail);
  detailRef.current = detail;
  // What the "remove the passage" dialog says while it is open: kept as it fades out, when the passage may be out.
  const passageTextRef = useRef("");
  // The dialog as it is now, for answers that come after it changed.
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;

  useEffect(() => () => clearTimeout(flashTimer.current), []);
  const openDialog = (type, data = {}) => setDialog({ type, open: true, revision: detailRef.current?.revision, ...data });
  const closeDialog = () => setDialog((prev) => (prev ? { ...prev, open: false } : prev));
  const isOpen = (type) => !!dialog?.open && dialog.type === type;

  // A variant of another subject (an old ?variant= after the subject changed): this subject's list instead.
  const otherSubject = !!detail?.folder?.uuid && detail.folder.uuid !== folder.uuid;
  useEffect(() => {
    if (otherSubject) onBack({ replace: true });
  }, [otherSubject]); // eslint-disable-line react-hooks/exhaustive-deps

  // The focus on the title once the variant is there: opened or created in this tab, or loaded by a retry (a retry
  // that failed again gives it back to its button). After Headless UI's own restore of a dialog that closed.
  useEffect(() => {
    if (!detail || !(retriedRef.current || (autoFocus && !focusedRef.current))) return;
    retriedRef.current = false;
    focusedRef.current = true;
    requestAnimationFrame(() => titleRef.current?.focus());
  }, [detail, autoFocus]);
  useEffect(() => {
    if (!error || !retriedRef.current) return;
    retriedRef.current = false;
    requestAnimationFrame(() => retryRef.current?.focus());
  }, [error]);

  const refs = useMemo(() => questionRefs(detail?.test_questions), [detail]);
  const passages = useMemo(() => passageNames(t, detail?.test_questions), [t, detail]);
  const quarters = matrix.grades.find((g) => g.grade === detail?.grade)?.quarters || [];
  const readOnly = !!detail?.locked;
  const variantBusy = pending.has("variant");
  const isBusy = useCallback((key) => variantBusy || pending.has(key), [pending, variantBusy]);
  const hrefWith = (change) => {
    const params = new URLSearchParams(location.search);
    change(params);
    return `${location.pathname}?${params.toString()}`;
  };
  const listHref = hrefWith((params) => params.delete("variant"));
  // The matrix tab, with the variant's grade (?grade=): the weeks it sends the user to fix are that grade's.
  const matrixHref = hrefWith((params) => {
    params.set("tab", "matrix");
    params.delete("variant");
    if (detail?.grade != null) params.set("grade", String(detail.grade));
  });

  // A place's dialog shows the variant as it was when it opened. When it is read again meanwhile, the dialog moves on
  // to that place as it is now, or closes when the place is gone: after someone else's change of the variant, or a
  // save of the matrix (a row's uuid changes with every save: the row is found again by its topic and subtopics, or
  // as its week's row of that topic).
  useEffect(() => {
    if (!detail || !dialog?.open || dialog.type !== "candidates") return;
    const { target, trigger } = dialog;
    const rows = detail.check?.rows || [];
    const variantChanged = String(since(dialog.revision)) !== String(detail.revision);
    const rowMoved = target.kind === "add" && !rows.some((item) => rowKey(item.row) === rowKey(target.row));
    if (!variantChanged && !rowMoved) return;
    let next = null;
    if (target.kind === "replace") {
      const tq = (detail.test_questions || []).find((row) => row.uuid === target.tq.uuid);
      if (tq && !tq.question?.passage?.uuid) next = { ...target, tq };
    } else {
      const entry = findRow(rows, target.row);
      if (entry?.row?.topic && (entry.planned?.[target.level] || 0) > (entry.actual?.[target.level] || 0)) {
        next = { ...target, row: entry.row, slotKey: movedSlotKey(target.slotKey, entry, target.level) };
      }
    }
    if (next) {
      setDialog((prev) => ({ ...prev, target: next, revision: detail.revision, refreshed: variantChanged ? "variant" : "matrix" }));
    } else {
      closeDialog();
      toast.warning(t(variantChanged ? "variants.errors.place_gone" : "variants.errors.row_not_found"));
      settleFocus(trigger, target.slotKey, nearPlace(target));
    }
  }, [detail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Someone else's change that refused a write (its before and after, from useVariant): listed above the composition.
  const noteChange = (err) => {
    const { before, after } = err?.variantChange || {};
    if (!before || !after) return;
    const changes = variantChanges(t, before, after);
    if (changes.lines.length) setChangeNotice(changes);
  };

  // A write refused: its questions named by their numbers in the variant as it is now (read again after a 409).
  const failed = (err, fallbackKey, extra = []) => {
    const message = variantErrorMessage(t, err, { refs: questionRefs(latest()?.test_questions, extra), fallbackKey });
    if (getApiErrorCode(err) === "variant_changed") {
      noteChange(err);
      // Writes queued behind the refused one are refused too: one message says it.
      toast.warning(message, { toastId: "variant_changed" });
    } else toast.error(message);
  };

  // A write of this page, decided on the variant as the page shows it (`base`: its revision, or the revision of the
  // dialog's variant): not as read again a moment ago and not yet on screen. Once it is done, someone else's earlier
  // change is no longer news, nor is a refused confirmation of the variant as it was.
  const run = (key, request, base = detailRef.current?.revision) =>
    write(key, request, base).then((data) => {
      setChangeNotice(null);
      setRefused(false);
      return data;
    });

  // After a write of a place: say it and light up the questions it brought.
  const done = (message, questionUuids) => {
    setAnnouncement(message);
    if (questionUuids.length) {
      setFlashIds(new Set(questionUuids));
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlashIds(new Set()), FLASH_MS);
    }
  };
  // Where the focus goes once a write is over (done or refused): the button that started it while it is still there
  // and on, else the place's first button, else `fallback` (an element, or a function that finds it then), else the
  // title. A dialog still fading out gives the focus back itself first: this waits until it has gone, and keeps a
  // focus it (or the user) put in the page.
  const settleFocus = (trigger, slotKey, fallback) => {
    let frames = 0;
    const step = () => {
      if (document.querySelector('[role="dialog"]') && frames < 90) {
        frames += 1;
        requestAnimationFrame(step);
        return;
      }
      const active = document.activeElement;
      if (document.querySelector('[role="dialog"]') || (active && active !== document.body && rootRef.current?.contains(active))) return;
      const place = slotKey ? rootRef.current?.querySelector(`[data-slot="${CSS.escape(slotKey)}"]`) : null;
      const target =
        trigger?.isConnected && !trigger.disabled
          ? trigger
          : place?.querySelector("button:not([disabled])") || (typeof fallback === "function" ? fallback() : fallback) || titleRef.current;
      target?.focus();
    };
    requestAnimationFrame(step);
  };
  // The focus's way back from a place's dialog whose place is gone (a fallback for settleFocus): the same week's
  // place of that level as the page shows it then (its first free one), else the composition's title.
  const nearPlace = (target) => () => {
    const { row, level } = target || {};
    const entry = row && (detailRef.current?.check?.rows || []).find((item) => weekKey(item.row) === weekKey(row) && item.planned?.[level] > 0);
    const filled = (entry?.test_questions?.[level] || []).length;
    const place = entry && rootRef.current?.querySelector(`[data-slot="${CSS.escape(placeKey(entry.row, level, filled < entry.planned[level] ? filled : 0))}"]`);
    return place?.querySelector("button:not([disabled])") || document.getElementById("variant-composition-title");
  };
  const numberOf = (data, questionUuid) => data?.test_questions?.find((tq) => tq.question?.uuid === questionUuid)?.order ?? "";

  // A dialog or the student's view opened from "⋯": once it is gone, the focus goes back to "⋯" (the item that opened
  // it is gone), unless something in the page has it.
  const overlayShown = !!dialog?.open || !!preview;
  useEffect(() => {
    if (overlayShown || !fromMenuRef.current) return;
    fromMenuRef.current = false;
    settleFocus(null, null, () => actionsRef.current?.querySelector('[data-testid="variant-actions"]'));
  }, [overlayShown]); // eslint-disable-line react-hooks/exhaustive-deps

  const runHeader = (name, request, base) => {
    setRunning(name);
    return run("variant", request, base).finally(() => setRunning(null));
  };

  const propose = (replace, base) => {
    setRefused(false);
    runHeader("propose", (revision) => proposeVariant(uuid, replace ? { revision, replace: true, seed: randomSeed() } : { revision }), base)
      .then((data) => {
        const proposed = data?.test_questions?.length || 0;
        const planned = data?.check?.planned?.count ?? 0;
        setProposal({ revision: data?.revision, proposed, planned, shortfalls: data?.shortfalls || [], reused: data?.reused?.length || 0 });
        setAnnouncement(t("variants.proposal.summary", { proposed, planned }));
      })
      .catch((err) => {
        failed(err, "variants.errors.propose_failed");
        if (TOPIC_CODES.includes(getApiErrorCode(err))) reload();
      });
  };
  // From its dialog, once: the second click of a double click finds it closed.
  const repropose = () => {
    if (!isOpen("repropose")) return;
    closeDialog();
    propose(true, dialog.revision);
  };

  // `trigger`: the check's "Confirm again", which goes away once confirmed (the header's button stays).
  const confirm = (trigger) => {
    if (variantBusy) return;
    runHeader("confirm", (revision) => confirmVariant(uuid, { revision }))
      .then(() => {
        setRefused(false);
        toast.success(t("variants.toast.confirmed"));
      })
      .catch((err) => {
        if (getApiErrorCode(err) === "variant_incomplete") {
          setCheck(err.response?.data?.check);
          setRefused(true);
          requestAnimationFrame(() => {
            checkRef.current?.scrollIntoView({ block: "start", behavior: reduceMotion() ? "auto" : "smooth" });
            checkRef.current?.focus({ preventScroll: true });
          });
          return;
        }
        failed(err, "variants.errors.confirm_failed");
        if (TOPIC_CODES.includes(getApiErrorCode(err))) reload();
      })
      .finally(() => {
        if (trigger) settleFocus(trigger, null, checkRef.current);
      });
  };

  const unconfirm = (reason) => {
    if (!isOpen("unconfirm")) return;
    closeDialog();
    runHeader("unconfirm", (revision) => unconfirmVariant(uuid, { revision, ...(reason ? { reason } : {}) }), dialog.revision)
      .then(() => toast.success(t("variants.toast.unconfirmed")))
      .catch((err) => failed(err, "variants.errors.unconfirm_failed"));
  };

  const saveSettings = (fields, base) =>
    run("variant", (revision) => updateSubjectVariant(uuid, { ...fields, revision }), base).catch((err) => {
      if (getApiErrorCode(err) === "variant_changed") noteChange(err);
      throw err;
    });

  const remove = async () => {
    setDeleting(true);
    try {
      await deleteSubjectVariant(uuid);
      toast.success(t("variants.toast.deleted"));
      closeDialog();
      // In place of the variant's entry: the browser's Back must not lead to the deleted variant.
      onBack({ replace: true });
    } catch (err) {
      failed(err, "variants.errors.delete_failed");
      // Put into a booklet meanwhile, or gone (deleted elsewhere, out of reach): the variant is read again; a gone
      // one leaves its page for "not found" with the way back.
      const gone = err?.response?.status === 404;
      if (gone) closeDialog();
      if (gone || getApiErrorCode(err) === "variant_in_booklet") reload();
    } finally {
      setDeleting(false);
    }
  };

  const openAdd = (entry, level, slotKey, trigger) => openDialog("candidates", { target: { kind: "add", row: entry.row, level, slotKey }, trigger });
  const openReplace = (tq, entry, level, slotKey, trigger) =>
    openDialog("candidates", { target: { kind: "replace", tq, row: entry.row, level, slotKey }, trigger });
  // The place asked for is no longer there (404): the dialog closes and the variant is read again; the focus goes
  // back to that place as it is now.
  const placeGone = (err) => {
    const { trigger, target } = dialog || {};
    closeDialog();
    toast.warning(variantErrorMessage(t, err));
    reload().finally(() => settleFocus(trigger, target?.slotKey, nearPlace(target)));
  };
  // Closed without a choice: Headless UI gives the focus back to the button that opened it, unless its place was
  // laid out anew meanwhile (the matrix saved: a new row uuid); then to that place as it is now.
  const closeCandidates = () => {
    const { trigger, target } = dialogRef.current || {};
    closeDialog();
    settleFocus(trigger, target?.slotKey, nearPlace(target));
  };
  // The dialog's row is no longer in the matrix (400 row_not_found: it was saved meanwhile): the variant is read
  // again, and the dialog moves on to that place as it is now or closes (the effect above). A read that fails, or
  // one that still has that row, closes it here.
  const rowStale = (err) => {
    const { trigger, target } = dialog || {};
    reload().then((fresh) => {
      if (fresh && !(fresh.check?.rows || []).some((item) => rowKey(item.row) === rowKey(target?.row))) return;
      if (!dialogRef.current?.open || dialogRef.current.target !== target) return;
      closeDialog();
      toast.warning(variantErrorMessage(t, err));
      settleFocus(trigger, target?.slotKey, nearPlace(target));
    });
  };

  // Once per choice: the second click of a double click finds the dialog closed.
  const choose = (question) => {
    if (!isOpen("candidates")) return;
    const { target, revision, trigger } = dialog;
    closeDialog();
    if (target.kind === "replace") {
      const { tq } = target;
      run(`tq:${tq.uuid}`, (rev) => replaceInVariant(uuid, { test_question: tq.uuid, question: question.uuid, revision: rev }), revision)
        .then(() => done(t("variants.done.replaced", { number: tq.order, code: question.code || "" }), [question.uuid]))
        .catch((err) => failed(err, "variants.errors.replace_failed", [question]))
        .finally(() => settleFocus(trigger, target.slotKey, nearPlace(target)));
    } else {
      run(target.slotKey, (rev) => addToVariant(uuid, { question: question.uuid, revision: rev }), revision)
        .then((data) => done(t("variants.done.added", { number: numberOf(data, question.uuid), code: question.code || "" }), [question.uuid]))
        .catch((err) => failed(err, "variants.errors.add_failed", [question]))
        .finally(() => settleFocus(trigger, target.slotKey, nearPlace(target)));
    }
  };

  const choosePassage = (passage, question) => {
    if (!isOpen("candidates")) return;
    const { target, revision, trigger } = dialog;
    closeDialog();
    run(target.slotKey, (rev) => addPassageToVariant(uuid, { passage: passage.uuid, revision: rev }), revision)
      .then((data) => {
        const added = (data?.test_questions || []).filter((tq) => tq.question?.passage?.uuid === passage.uuid).map((tq) => tq.question.uuid);
        done(t("variants.done.passage_added", { name: passage.name || "", count: added.length }), added);
      })
      .catch((err) => {
        // The server's slot_full names no place (the dialog said which ones the passage needs), and is not about
        // the place the dialog was opened for.
        if (getApiErrorCode(err) === "slot_full") toast.error(t("variants.errors.passage_slot_full"));
        else failed(err, "variants.errors.add_failed", [question]);
      })
      .finally(() => settleFocus(trigger, target.slotKey, nearPlace(target)));
  };

  const removeQuestion = (tq, slotKey, trigger) =>
    run(`tq:${tq.uuid}`, (revision) => removeFromVariant(uuid, { test_question: tq.uuid, revision }))
      .then(() => done(t("variants.done.removed", { number: tq.order, code: tq.question?.code || "" }), []))
      .catch((err) => failed(err, "variants.errors.remove_failed"))
      .finally(() => settleFocus(trigger, slotKey));

  // A passage in words: by its name, or by its questions' codes in the variant when it has none (one the user no
  // longer reads comes as its uuid alone). `key` has `<key>_code` and `<key>_codes` twins for that (byCodes).
  const passageWords = (key, passage, testQuestions) => {
    const codes = passageCodes(testQuestions, passage?.uuid);
    const name = passage?.name?.trim();
    return name ? t(key, { name, count: codes.length }) : byCodes(t, key, codes);
  };

  const removePassage = () => {
    if (!isOpen("remove_passage")) return;
    const { passage, revision, trigger, slotKey } = dialog;
    // Named now: once it is out, the variant no longer has its questions' codes.
    const removed = passageWords("variants.done.passage_removed", passage, detailRef.current?.test_questions);
    closeDialog();
    run(`passage:${passage.uuid}`, (rev) => removePassageFromVariant(uuid, { passage: passage.uuid, revision: rev }), revision)
      .then(() => done(removed, []))
      .catch((err) => failed(err, "variants.errors.remove_failed"))
      .finally(() => settleFocus(trigger, slotKey));
  };

  const keepOrder = (questionUuid, on) =>
    run(`keep:${questionUuid}`, (revision, current) => {
      const list = new Set(current?.keep_order || []);
      if (on) list.add(questionUuid);
      else list.delete(questionUuid);
      return updateSubjectVariant(uuid, { keep_order: [...list], revision });
    })
      .then(() => {
        const ref = refs.get(questionUuid);
        const question = ref ? t("variants.question_ref", { number: ref.number, code: ref.code }) : "";
        setAnnouncement(t(on ? "variants.keep.on_done" : "variants.keep.off_done", { question }));
      })
      .catch((err) => failed(err, "variants.errors.failed"));

  const backLink = (
    <Link to={listHref} className={`${linkButtonClass} -ml-1.5 max-sm:min-h-11`}>
      <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
      {t("variants.back")}
    </Link>
  );

  if (loading && !detail) {
    return (
      <div className="space-y-4">
        {backLink}
        <div className={`${cardClass} flex flex-col items-center justify-center py-16`} role="status">
          <Spinner className="mb-3 h-8 w-8" />
          <p className="text-sm text-slate-500">{t("variants.loading")}</p>
        </div>
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div className="space-y-4">
        {backLink}
        <div className={`${cardClass} flex flex-col items-center px-6 py-14 text-center`} role="alert">
          <div className="mb-3 rounded-full bg-red-50 p-3 text-red-500">
            <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <p className="max-w-md text-sm font-medium text-slate-900">
            {isOutOfReach(error) ? t("variants.out_of_reach") : variantErrorMessage(t, error, { fallbackKey: "variants.load_failed" })}
          </p>
          {!isOutOfReach(error) && (
            <button
              ref={retryRef}
              type="button"
              onClick={() => {
                retriedRef.current = true;
                retry();
              }}
              className={`${secondaryButtonClass} mt-4`}
            >
              <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
              {t("test_bank.builder.retry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  const check = detail.check || {};
  const empty = !detail.test_questions?.length;
  const confirmed = detail.status === "confirmed";
  // As many as the check lists (an empty variant's places are one line there).
  const blockingCount = blockingLines(check).count;
  // The grade's matrix plans nothing (no rows for it, or none planned): there is nothing to propose. Nor when only
  // its rows without a topic plan anything (the server would answer 400 matrix_without_topics).
  const noTopics =
    (check.planned?.count ?? 0) > 0 &&
    (check.rows || []).length > 0 &&
    !(check.rows || []).some((entry) => !hasNoTopic(entry.row) && plannedCount(entry) > 0);
  const noPlan = (check.planned?.count ?? 0) === 0 || noTopics;
  const activeBooklets = (detail.booklets || []).filter((booklet) => booklet.status === "active");
  // A header button waiting for a write keeps the focus (aria-disabled, see variantUi) and does nothing.
  const whenFree = (action) => () => {
    if (!variantBusy) action();
  };
  if (isOpen("remove_passage")) passageTextRef.current = passageWords("variants.remove_passage.text", dialog.passage, detail.test_questions);

  // The header's actions, laid out as FolderBar's and the BTS pages' (OverflowMenu's breakpoint, md): the status's
  // step in sight (Propose for an empty variant, Confirm for a draft, Back to draft once confirmed, Student's view for
  // a read-only one), the everyday ones quiet beside it from md up, the rare ones in "⋯"; phones keep the step and
  // "⋯" in one row, with the everyday ones in "⋯" too. `write`: it waits while a write runs; `running`: the write it
  // starts (its spinner).
  const action = {
    propose: { key: "propose", label: t("variants.actions.propose"), Icon: SparklesIcon, onClick: whenFree(() => propose(false)), disabled: noPlan, write: true, running: "propose", primary: true },
    again: { key: "again", label: t("variants.actions.propose_again"), Icon: ArrowPathIcon, onClick: whenFree(() => openDialog("repropose")), disabled: noPlan, write: true, running: "propose" },
    confirm: { key: "confirm", label: t("variants.actions.confirm"), Icon: CheckBadgeIcon, onClick: () => confirm(), write: true, running: "confirm", primary: true },
    unconfirm: { key: "unconfirm", label: t("variants.actions.unconfirm"), Icon: ArrowUturnLeftIcon, onClick: whenFree(() => openDialog("unconfirm")), write: true, running: "unconfirm" },
    student: { key: "student", label: t("variants.actions.student_view"), Icon: EyeIcon, onClick: () => setPreview(studentView(t, detail)) },
    settings: { key: "settings", label: t("variants.actions.settings"), Icon: Cog6ToothIcon, onClick: whenFree(() => openDialog("settings")), write: true },
    remove: { key: "delete", label: t("buttons.delete"), Icon: TrashIcon, onClick: whenFree(() => openDialog("delete")), write: true, danger: true },
  };
  let step = null;
  let everyday = [];
  let rare = [];
  if (readOnly) step = empty ? null : action.student;
  else if (empty) [step, rare] = [action.propose, [action.settings, action.remove]];
  else if (confirmed) [step, everyday, rare] = [action.unconfirm, [action.student], [action.again, action.settings, action.remove]];
  else [step, everyday, rare] = [action.confirm, [action.again, action.student], [action.settings, action.remove]];
  const quiet = wide ? everyday : [];
  // A dialog opened from "⋯" gives the focus back to "⋯" when it closes (its item is gone by then).
  const menuItems = (wide ? rare : [...everyday, ...rare]).map((item) => ({
    ...item,
    disabled: item.disabled || (item.write && variantBusy),
    onClick: () => {
      fromMenuRef.current = true;
      item.onClick();
    },
  }));
  // Confirm while the check has blocking issues: dimmed (aria-disabled) but in the tab order, and the line under the
  // actions says why. A press sends nothing: the check says the variant was not confirmed and takes the focus, and the
  // variant is read again (a page that is out of date gets the server's state: Confirm comes back once nothing blocks).
  const blocked = step === action.confirm && !check.ok && blockingCount > 0;
  const actionIcon = (item) =>
    running && running === item.running ? <Spinner className="h-4 w-4" /> : <item.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />;
  // A write started from "⋯" has no button of its own on screen: the line under the actions shows it runs.
  const shownRunning = [step, ...quiet].some((item) => item?.running && item.running === running);
  const goToCheck = () => {
    checkRef.current?.scrollIntoView({ block: "start", behavior: reduceMotion() ? "auto" : "smooth" });
    checkRef.current?.focus({ preventScroll: true });
  };
  const pressBlocked = whenFree(() => {
    setRefused(true);
    goToCheck();
    reload();
  });
  // The grade's "nothing to propose" banner, with its way to the matrix (the composition's weeks then leave theirs out).
  const planBanner = !readOnly && noPlan;

  return (
    // relative: the sr-only texts and KaTeX's hidden MathML inside stay in <main>'s scroll box (not the window's).
    <div ref={rootRef} className="relative min-w-0 space-y-4">
      {backLink}
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>

      <header aria-labelledby="variant-title" className={`${cardClass} p-4 sm:p-5`}>
        <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-start 2xl:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {/* The guide's "?" right after the name (the same guide as the list's). */}
              <div className="flex min-w-0 items-center gap-1.5">
                <h3 id="variant-title" ref={titleRef} tabIndex={-1} className="min-w-0 text-xl font-semibold tracking-tight text-slate-900 outline-none [overflow-wrap:anywhere]">
                  {detail.name}
                </h3>
                <HelpButton id="variants" title={t("help.variants.title")} className="-my-2 md:-my-1">
                  <VariantsGuide />
                </HelpButton>
              </div>
              <VariantStatusBadge status={detail.status} />
              {readOnly && <LockedBadge />}
            </div>
            <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
              <div className="flex items-baseline gap-1.5">
                <dt className="text-slate-500">{t("variants.fields.grade")}</dt>
                <dd className="font-medium text-slate-900">{t("variants.grade", { grade: detail.grade })}</dd>
              </div>
              <div className="flex min-w-0 items-baseline gap-1.5">
                <dt className="shrink-0 text-slate-500">{t("variants.fields.heading")}</dt>
                <dd className="min-w-0 font-semibold text-slate-900 [overflow-wrap:anywhere]">
                  {detail.heading || <span className="font-normal text-amber-700">{t("variants.no_heading")}</span>}
                </dd>
              </div>
              <Stat label={t("variants.list.col_questions")} value={check.actual?.count ?? detail.questions_count} planned={check.planned?.count} />
              <Stat label={t("variants.list.col_points")} value={check.actual?.points ?? detail.points_total} planned={check.planned?.points} />
              <div className="flex items-baseline gap-1.5">
                <dt className="text-slate-500">{t("variants.fields.updated")}</dt>
                <dd className="tabular-nums text-slate-700">{formatDateTime(detail.updated, language)}</dd>
              </div>
            </dl>
            {confirmed && detail.confirmed_at && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-emerald-800">
                <CheckBadgeIcon className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                {t("variants.confirmed_by", { name: accountName(detail.confirmed_by) || "—", date: formatDateTime(detail.confirmed_at, language) })}
              </p>
            )}
            {detail.description && <p className="mt-2 max-w-4xl whitespace-pre-line text-sm text-slate-600">{detail.description}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {detail.calculator_is_allowed && (
                <span className={`${badgeClass} bg-slate-100 text-slate-700 ring-slate-500/20`}>{t("test_bank.form.calculator_allowed")}</span>
              )}
              {detail.periodic_table_is_allowed && (
                <span className={`${badgeClass} bg-slate-100 text-slate-700 ring-slate-500/20`}>{t("test_bank.form.periodic_table_allowed")}</span>
              )}
              {detail.booklets?.length > 0 && (
                <>
                  <span className="ml-1 text-xs text-slate-500">{t("variants.booklets")}:</span>
                  <BookletChips booklets={detail.booklets} />
                </>
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-1 2xl:shrink-0 2xl:items-end">
            {(step || quiet.length > 0 || menuItems.length > 0) && (
              <div ref={actionsRef} className="flex items-center gap-2 max-md:w-full md:flex-wrap 2xl:justify-end">
                {quiet.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={item.onClick}
                    disabled={!!item.disabled}
                    aria-disabled={(item.write && variantBusy) || undefined}
                    aria-describedby={item.disabled && noPlan ? noPlanId : undefined}
                    className={headerQuietClass}
                  >
                    {actionIcon(item)}
                    {item.label}
                  </button>
                ))}
                {/* The step and "⋯" keep together when the row wraps: only the quiet ones go to a line of their own. */}
                <div className="flex items-center gap-2 max-md:flex-1 md:shrink-0">
                  {/* The step: always this one <button>, so the focus stays on it when it turns into the next step. */}
                  {step && (
                    <button
                      type="button"
                      onClick={blocked ? pressBlocked : step.onClick}
                      disabled={!!step.disabled}
                      aria-disabled={(step.write && variantBusy) || blocked || undefined}
                      aria-describedby={blocked ? checkLineId : step.disabled && noPlan ? noPlanId : undefined}
                      title={blocked ? t("variants.confirm_blocked", { count: blockingCount }) : undefined}
                      className={step.primary ? headerPrimaryClass : headerQuietClass}
                    >
                      {actionIcon(step)}
                      {step.label}
                    </button>
                  )}
                  <OverflowMenu items={menuItems} label={t("variants.actions.more")} testId="variant-actions" />
                </div>
              </div>
            )}
            {running && !shownRunning && (
              <p role="status" className="flex items-center gap-1.5 py-1 text-sm text-slate-500">
                <Spinner className="h-4 w-4" />
                {t("variants.saving")}
              </p>
            )}
            {/* What the check says, as a way to it: under the actions, where it tells why Confirm is dimmed; next to
                the check itself (2xl) only then. An empty variant's places are told by its hint below. */}
            {!empty && (
              <button
                id={checkLineId}
                type="button"
                onClick={goToCheck}
                className={`${checkLineClass(check.ok)} -ml-1.5 max-sm:min-h-11 2xl:-mr-1.5 2xl:ml-0 ${blocked ? "" : "2xl:hidden"}`}
              >
                {check.ok ? <CheckCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" /> : <XCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                {check.ok
                  ? t(confirmed ? "variants.check.passed" : "variants.check.ok")
                  : blocked
                    ? t("variants.confirm_blocked", { count: blockingCount })
                    : t("variants.check.blocking_count", { count: blockingCount })}
              </button>
            )}
          </div>
        </div>
      </header>

      {readOnly && (
        <div role="note" className="flex items-start gap-3 rounded-2xl border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-800">
          <LockClosedIcon className="mt-0.5 h-5 w-5 shrink-0 text-slate-600" aria-hidden="true" />
          <div className="min-w-0">
            <p className="font-semibold">{t("variants.locked_banner.title")}</p>
            <p className="mt-0.5">
              {t("variants.locked_banner.text", { names: (activeBooklets.length ? activeBooklets : detail.booklets || []).map((b) => b.name).join(", ") })}
            </p>
          </div>
        </div>
      )}

      {changeNotice && <ChangeNotice notice={changeNotice} onClose={() => setChangeNotice(null)} />}

      {/* What the proposal could not fill, until the variant changes again (its places say it then). */}
      {proposal && proposal.revision === detail.revision && (
        <ProposalPanel proposal={proposal} folderUuid={folder.uuid} matrixHref={matrixHref} onClose={() => setProposal(null)} />
      )}

      {planBanner ? (
        <div id={noPlanId} className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 sm:flex-row sm:items-center">
          <TableCellsIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="min-w-0 flex-1">{t(noTopics ? "variants.no_topics" : "variants.no_plan")}</p>
          <Link to={matrixHref} className={`${linkButtonClass} -ml-1.5 self-start max-sm:min-h-11 sm:ml-0 sm:self-auto`}>
            <TableCellsIcon className="h-4 w-4" aria-hidden="true" />
            {t("variants.list.open_matrix")}
          </Link>
        </div>
      ) : (
        empty &&
        !readOnly &&
        !variantBusy && (
          <div className="flex flex-col gap-3 rounded-2xl border border-blue-200 bg-blue-50/70 px-4 py-3 text-sm text-blue-950 sm:flex-row sm:items-center">
            <SparklesIcon className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
            <p className="min-w-0 flex-1">{t("variants.empty_hint")}</p>
          </div>
        )
      )}

      <div className="grid min-w-0 gap-4 2xl:grid-cols-[minmax(0,1fr)_24rem] min-[1920px]:grid-cols-[minmax(0,1fr)_28rem]">
        <div className="min-w-0">
          <VariantComposition
            detail={detail}
            quarters={quarters}
            folderUuid={folder.uuid}
            matrixHref={planBanner ? null : matrixHref}
            readOnly={readOnly}
            isBusy={isBusy}
            flashIds={flashIds}
            changes={changeNotice}
            onAdd={openAdd}
            onReplace={openReplace}
            onRemove={removeQuestion}
            onRemovePassage={(passage, slotKey, trigger) => openDialog("remove_passage", { passage, slotKey, trigger })}
            onView={(tq) => openDialog("question", { tq })}
          />
        </div>
        <aside className="min-w-0 scroll-mt-4 2xl:sticky 2xl:top-4 2xl:max-h-[calc(100vh-2rem)] 2xl:self-start 2xl:overflow-y-auto">
          <VariantCheck
            ref={checkRef}
            detail={detail}
            refs={refs}
            passages={passages}
            folderUuid={folder.uuid}
            matrixHref={matrixHref}
            readOnly={readOnly}
            refused={refused}
            keepBusy={(questionUuid) => pending.has(`keep:${questionUuid}`)}
            onKeepOrder={keepOrder}
            onConfirmAgain={confirm}
            confirmBusy={variantBusy}
          />
        </aside>
      </div>

      <CandidatesDialog
        isOpen={isOpen("candidates")}
        variantUuid={uuid}
        folderUuid={folder.uuid}
        target={dialog?.type === "candidates" ? dialog.target : null}
        rows={check.rows}
        refs={refs}
        refreshed={dialog?.type === "candidates" ? dialog.refreshed || null : null}
        onClose={closeCandidates}
        onChoose={choose}
        onChoosePassage={choosePassage}
        onGone={placeGone}
        onStale={rowStale}
      />
      <SettingsDialog isOpen={isOpen("settings")} variant={detail} onClose={closeDialog} onSave={saveSettings} />
      <UnconfirmDialog isOpen={isOpen("unconfirm")} onClose={closeDialog} onConfirm={unconfirm} />
      <DeleteDialog isOpen={isOpen("delete")} variant={detail} busy={deleting} onClose={closeDialog} onConfirm={remove} />
      <ConfirmDialog
        isOpen={isOpen("repropose")}
        title={t("variants.repropose.title")}
        text={confirmed ? t("variants.repropose.text_confirmed") : t("variants.repropose.text")}
        confirmLabel={t("variants.actions.propose_again")}
        onClose={closeDialog}
        onConfirm={repropose}
      />
      <ConfirmDialog
        isOpen={isOpen("remove_passage")}
        title={t("variants.remove_passage.title")}
        text={passageTextRef.current}
        confirmLabel={t("variants.actions.remove_passage")}
        danger
        onClose={closeDialog}
        onConfirm={removePassage}
      />
      <QuestionDialog isOpen={isOpen("question")} tq={dialog?.type === "question" ? dialog.tq : null} onClose={closeDialog} />
      <TestPreview
        open={!!preview}
        test={preview}
        badge={t("variants.preview.badge")}
        hint={t("variants.preview.hint")}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}
